import type { FC, ReactNode } from 'react'
import type { Move, Square } from 'chess.js'
import type { BoardPiece } from '@/lib/chess'

import { useEffect, useRef, useState } from 'react'
import { Chess } from 'chess.js'

import {
    CircleAlert,
    CircleCheck,
    Eye,
    Lightbulb,
    RotateCcw,
    Sparkles,
    Target
} from 'lucide-react'

import { cn } from '@/lib/utils'
import NeonBoard from '@/components/chess/neon-board'
import NeonLink from '@/components/landing/neon-link'
import SectionHeading from '@/components/landing/section-heading'
import { useBoardTheme } from '@/components/chess/board-theme'
import { applyMove, findKing, piecesFromFen } from '@/lib/chess'
import { OPERA_PUZZLE } from '@/constants/games'

type Phase = 'ready' | 'reverting' | 'replying' | 'finish' | 'solved' | 'revealing' | 'revealed'
type Tone = 'neutral' | 'error' | 'info' | 'success'

interface PuzzleView {
    pieces: BoardPiece[]
    lastMove: { from: Square, to: Square } | null
    check: Square | null
    sans: string[]
}

const INTRO = "Your move. Tap a white piece to see where it can go, then tap a glowing square."

const INITIAL_PIECES = piecesFromFen(OPERA_PUZZLE.fen)

const INITIAL_VIEW: PuzzleView = {
    pieces: INITIAL_PIECES,
    lastMove: null,
    check: null,
    sans: []
}

const TONE_STYLES: Record<Tone, { box: string, icon: string }> = {
    neutral: { box: 'border-white/10 bg-white/[0.03]', icon: 'text-neon-cyan' },
    info: { box: 'border-neon-cyan/35 bg-neon-cyan/5', icon: 'text-neon-cyan' },
    error: { box: 'border-neon-pink/45 bg-neon-pink/5', icon: 'text-neon-pink' },
    success: { box: 'border-neon-lime/45 bg-neon-lime/5', icon: 'text-neon-lime' }
}

const TONE_ICONS: Record<Tone, typeof Target> = {
    neutral: Target,
    info: Sparkles,
    error: CircleAlert,
    success: CircleCheck
}

const stripSan = (san: string): string => san.replace(/[+#?!]/g, '')

const computeView = (chess: Chess): PuzzleView => {
    const history = chess.history({ verbose: true })
    const pieces = history.reduce((current, move) => applyMove(current, move), INITIAL_PIECES)
    const last = history[history.length - 1]

    return {
        pieces,
        lastMove: last ? { from: last.from, to: last.to } : null,
        check: chess.inCheck() ? findKing(pieces, chess.turn()) : null,
        sans: history.map(move => move.san)
    }
}

const formatLog = (sans: string[]): string => {
    return sans
        .map((san, index) => (index % 2 === 0 ? `${OPERA_PUZZLE.firstMoveNumber + index / 2}. ${san}` : san))
        .join(' ')
}

const PuzzleSection: FC = (): ReactNode => {
    const { theme } = useBoardTheme()
    const chessRef = useRef<Chess | null>(null)
    const timers = useRef<number[]>([])

    const [view, setView] = useState<PuzzleView>(INITIAL_VIEW)
    const [phase, setPhase] = useState<Phase>('ready')
    const [feedback, setFeedback] = useState<{ tone: Tone, text: string }>({ tone: 'neutral', text: INTRO })
    const [selected, setSelected] = useState<Square | null>(null)
    const [targets, setTargets] = useState<Square[]>([])
    const [hintOn, setHintOn] = useState(false)
    const [misses, setMisses] = useState(0)

    useEffect(() => {
        const pending = timers.current
        return () => pending.forEach(id => window.clearTimeout(id))
    }, [])

    const getChess = (): Chess => {
        if (!chessRef.current) chessRef.current = new Chess(OPERA_PUZZLE.fen)
        return chessRef.current
    }

    const schedule = (callback: () => void, delay: number) => {
        const id = window.setTimeout(() => {
            const index = timers.current.indexOf(id)
            if (index !== -1) timers.current.splice(index, 1)
            callback()
        }, delay)

        timers.current.push(id)
    }

    const clearTimers = () => {
        timers.current.forEach(id => window.clearTimeout(id))
        timers.current.length = 0
    }

    const clearSelection = () => {
        setSelected(null)
        setTargets([])
    }

    const sync = () => setView(computeView(getChess()))

    const canMove = phase === 'ready' || phase === 'finish'
    const stage = phase === 'finish' ? 1 : 0
    const finished = phase === 'solved' || phase === 'revealed'
    const busy = phase === 'reverting' || phase === 'replying' || phase === 'revealing'

    const playMove = (from: Square, to: Square) => {
        const chess = getChess()
        let move: Move

        try {
            move = chess.move({ from, to, promotion: 'q' })
        }
        catch {
            return
        }

        clearSelection()
        setHintOn(false)
        sync()

        const plyIndex = chess.history().length - 1
        const correct = plyIndex === 0
            ? stripSan(move.san) === stripSan(OPERA_PUZZLE.solution[0])
            : chess.isCheckmate()

        if (!correct) {
            setMisses(count => count + 1)
            setPhase('reverting')

            if (plyIndex === 0) {
                setFeedback({
                    tone: 'error',
                    text: move.san.includes('+')
                        ? `${move.san} is a check, but Black slips away. Look for a check that leaves Black only one answer.`
                        : `Not quite. ${move.san} gives Black time to regroup. Try again.`
                })
            }
            else {
                setFeedback({ tone: 'error', text: `${move.san} isn't mate. There's a move that is.` })
            }

            schedule(() => {
                chess.undo()
                sync()
                setPhase(plyIndex === 0 ? 'ready' : 'finish')
            }, 1100)

            return
        }

        if (plyIndex === 0) {
            setPhase('replying')
            setFeedback({ tone: 'info', text: "Brilliant. A queen sacrifice with check, and Black has exactly one way out." })

            schedule(() => {
                try {
                    chess.move(OPERA_PUZZLE.solution[1])
                }
                catch {
                    const [fallback] = chess.moves()
                    if (fallback) chess.move(fallback)
                }

                sync()
                setPhase('finish')
                setFeedback({ tone: 'info', text: "Black had to take the queen. Now finish it in one move." })
            }, 850)

            return
        }

        setPhase('solved')
        setFeedback({
            tone: 'success',
            text: misses === 0
                ? "Checkmate on your first try. That's exactly how Morphy finished it."
                : `Checkmate. You found Morphy's finish after ${misses} ${misses === 1 ? 'miss' : 'misses'}.`
        })
    }

    const handleSquareClick = (square: Square) => {
        if (!canMove) return

        const chess = getChess()

        if (selected && targets.includes(square)) {
            playMove(selected, square)
            return
        }

        const piece = chess.get(square)

        if (piece && piece.color === chess.turn()) {
            if (selected === square) {
                clearSelection()
                return
            }

            setSelected(square)
            setTargets(chess.moves({ square, verbose: true }).map(move => move.to))
            return
        }

        clearSelection()
    }

    const showHint = () => {
        if (!canMove) return

        setHintOn(true)
        setFeedback({ tone: 'info', text: OPERA_PUZZLE.hints[stage].text })
    }

    const reset = () => {
        clearTimers()
        getChess().load(OPERA_PUZZLE.fen)
        sync()
        clearSelection()
        setHintOn(false)
        setMisses(0)
        setPhase('ready')
        setFeedback({ tone: 'neutral', text: INTRO })
    }

    const revealSolution = () => {
        clearTimers()

        const chess = getChess()
        chess.load(OPERA_PUZZLE.fen)

        sync()
        clearSelection()
        setHintOn(false)
        setPhase('revealing')
        setFeedback({ tone: 'info', text: "Watch closely. Here's how Morphy did it." })

        OPERA_PUZZLE.solution.forEach((san, index) => {
            schedule(() => {
                try {
                    chess.move(san)
                }
                catch {
                    return
                }

                sync()

                if (index === OPERA_PUZZLE.solution.length - 1) {
                    setPhase('revealed')
                    setFeedback({
                        tone: 'success',
                        text: "Qb8+ drags the knight off d7, then the rook mates on the open file, guarded by the bishop on g5."
                    })
                }
            }, 700 + index * 1100)
        })
    }

    const FeedbackIcon = TONE_ICONS[feedback.tone]
    const sideToMove = finished ? 'Checkmate' : phase === 'replying' ? 'Black to move' : 'White to move'

    const actionClass = 'inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-medium text-foreground/90 transition-colors hover:border-white/35 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan disabled:pointer-events-none disabled:opacity-40'

    return (
        <section id='puzzle' aria-labelledby='puzzle-title' className='relative scroll-mt-20 py-24 sm:py-32'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 overflow-hidden'>
                <div className='absolute -left-32 top-1/4 size-[28rem] rounded-full bg-neon-violet/15 blur-[120px]' />
            </div>

            <div className='mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:gap-x-16 lg:gap-y-8'>
                <SectionHeading
                    id='puzzle-title'
                    eyebrow='Featured puzzle'
                    accent='pink'
                    title={<>Find <span className='text-neon-pink glow-text-pink'>Morphy&apos;s</span> finish.</>}
                    description="Paris, 1858. Paul Morphy played this game from a box at the opera, in the middle of a performance. White to move and mate in two."
                    className='lg:col-start-2 lg:row-start-1 lg:self-end'
                />

                <div className='relative mx-auto w-full max-w-[34rem] lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:ml-0 lg:self-center'>
                    <div className='rounded-[1.75rem] border border-white/10 bg-[#080817]/80 p-3.5 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.8)] backdrop-blur-md sm:p-5'>
                        <div className='mb-4 flex items-center justify-between gap-3 px-1'>
                            <span className='font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-neon-pink'>
                                Mate in 2
                            </span>

                            <span className={cn('font-mono text-[11px] uppercase tracking-[0.2em]', finished ? 'text-neon-lime' : 'text-foreground/80')}>
                                {sideToMove}
                            </span>
                        </div>

                        <div className='relative'>
                            <NeonBoard
                                pieces={view.pieces}
                                theme={theme}
                                label='Puzzle board. White to move and mate in two.'
                                lastMove={view.lastMove}
                                checkSquare={view.check}
                                selected={selected}
                                targets={targets}
                                hintSquare={hintOn && canMove ? OPERA_PUZZLE.hints[stage].square : null}
                                onSquareClick={handleSquareClick}
                                players={{ white: OPERA_PUZZLE.white, black: OPERA_PUZZLE.blackShort }}
                            />

                            {finished && (
                                <div className='pointer-events-none absolute inset-x-0 top-6 flex justify-center'>
                                    <span className='animate-neon-pop rounded-full border border-neon-lime/60 bg-[#07130a]/90 px-5 py-2 font-heading text-sm font-semibold uppercase tracking-[0.2em] text-neon-lime glow-text-lime shadow-[0_0_30px_rgb(182_255_59/0.45)]'>
                                        Checkmate
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className='space-y-6 lg:col-start-2 lg:row-start-2'>
                    <ul className='flex flex-wrap gap-2'>
                        {['Mate in 2', 'White to move', 'The Opera Game'].map(chip => (
                            <li key={chip} className='rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-xs text-muted-foreground'>
                                {chip}
                            </li>
                        ))}
                    </ul>

                    <div role='status' aria-live='polite' className={cn('rounded-2xl border p-5 transition-colors duration-300', TONE_STYLES[feedback.tone].box)}>
                        <div className='flex items-start gap-3'>
                            <FeedbackIcon className={cn('mt-0.5 size-5 shrink-0', TONE_STYLES[feedback.tone].icon)} />

                            <p className='text-base leading-relaxed text-foreground/90'>
                                {feedback.text}
                            </p>
                        </div>

                        <p className='mt-4 border-t border-white/5 pt-4 font-mono text-sm text-muted-foreground'>
                            {view.sans.length ? formatLog(view.sans) : `${OPERA_PUZZLE.firstMoveNumber}. ?`}
                        </p>
                    </div>

                    <div className='flex flex-wrap gap-3'>
                        <button type='button' onClick={showHint} disabled={!canMove || hintOn} className={actionClass}>
                            <Lightbulb className='size-4 text-neon-lime' />
                            Hint
                        </button>

                        <button type='button' onClick={reset} disabled={phase === 'ready' && view.sans.length === 0 && !hintOn} className={actionClass}>
                            <RotateCcw className='size-4 text-neon-cyan' />
                            Reset
                        </button>

                        <button type='button' onClick={revealSolution} disabled={busy || finished} className={actionClass}>
                            <Eye className='size-4 text-neon-pink' />
                            Show solution
                        </button>
                    </div>

                    {finished && (
                        <div className='animate-neon-pop flex flex-col gap-4 rounded-2xl border border-neon-cyan/30 bg-neon-cyan/5 p-5 sm:flex-row sm:items-center sm:justify-between'>
                            <p className='text-sm leading-relaxed text-foreground/90'>
                                Sharp eyes. Ready for a full game? Take on the computer or a friend right now.
                            </p>

                            <NeonLink href='#play' size='sm' className='shrink-0'>
                                Play now
                            </NeonLink>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default PuzzleSection
